import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { useChatInput } from '../context/ChatInputContext';
import memoryApi from '../api/memoryApi';
import { ProjectsGrid } from '../components/projects';

/**
 * ProjectsPage - Shows all projects in a grid or empty state
 * URL: /chatty/projects
 */
const ProjectsPage = ({ sidebarOpen, setSidebarOpen }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { refreshSessions } = useChatInput();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  // Load projects on mount
  useEffect(() => {
    const loadProjects = async () => {
      if (!user) return;

      try {
        const projectList = await memoryApi.listProjects();
        setProjects(projectList || []);
      } catch (error) {
        console.error('Error loading projects:', error);
        setProjects([]);
      } finally {
        setLoading(false);
      }
    };

    loadProjects();
  }, [user]);

  const handleSelectProject = (project) => {
    navigate(`/chatty/projects/${project.project_id}`);
  };

  const handleCreateProject = async (title, description) => {
    try {
      const newProject = await memoryApi.createProject(title, description);
      setProjects(prev => [newProject, ...prev]);
      // Navigate to the new project
      navigate(`/chatty/projects/${newProject.project_id}`);
    } catch (error) {
      console.error('Failed to create project:', error);
      throw error;
    }
  };

  const handleDeleteProject = async (projectId) => {
    try {
      // Delete project AND all its sessions
      await memoryApi.deleteProjectWithSessions(projectId);
      setProjects(prev => prev.filter(p => p.project_id !== projectId));
      // Refresh sidebar sessions in case any were affected
      refreshSessions();
    } catch (error) {
      console.error('Failed to delete project:', error);
      throw error;
    }
  };

  const handleRenameProject = async (projectId, title, description) => {
    try {
      await memoryApi.renameProject(projectId, title, description);
      setProjects(prev => prev.map(p =>
        p.project_id === projectId ? { ...p, title, description } : p
      ));
    } catch (error) {
      console.error('Failed to rename project:', error);
      throw error;
    }
  };

  if (loading) {
    return (
      <div
        className="w-full overflow-hidden"
        style={{
          height: '100vh',
          backgroundColor: 'var(--bg-primary)'
        }}
      >
        <div
          className="w-full flex flex-col"
          style={{
            paddingTop: 'var(--navbar-height)',
            paddingBottom: 'var(--footer-height)',
            height: '100vh'
          }}
        >
          <div className="flex flex-col h-full animate-pulse">
            {/* Header skeleton */}
            <div
              className="flex items-center justify-between px-4 lg:px-6 py-4 border-b"
              style={{ borderColor: 'var(--border-color, rgba(255,255,255,0.1))' }}
            >
              <div className="w-6 lg:hidden" />
              <div className="h-6 w-24 rounded-lg lg:ml-12" style={{ backgroundColor: 'var(--bg-secondary)' }} />
              <div className="w-6 h-6 rounded-full lg:hidden" style={{ backgroundColor: 'var(--bg-secondary)' }} />
              <div className="hidden lg:block h-9 w-28 rounded-lg" style={{ backgroundColor: 'var(--bg-secondary)' }} />
            </div>

            {/* Content skeleton */}
            <div className="flex-1 p-4 lg:p-6">
              <div className="max-w-4xl mx-auto">
                {/* Search skeleton */}
                <div className="h-12 rounded-xl mb-4" style={{ backgroundColor: 'var(--bg-secondary)' }} />

                {/* Sort by skeleton */}
                <div className="flex justify-end items-center gap-2 mb-4">
                  <div className="h-4 w-14 rounded" style={{ backgroundColor: 'var(--bg-secondary)' }} />
                  <div className="h-8 w-32 rounded-lg" style={{ backgroundColor: 'var(--bg-secondary)' }} />
                </div>

                {/* Grid skeleton */}
                <div className="grid gap-3 sm:gap-5 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
                  {[1, 2, 3, 4, 5, 6].map(i => (
                    <div
                      key={i}
                      className="h-32 rounded-xl"
                      style={{ backgroundColor: 'var(--bg-secondary)' }}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="w-full overflow-hidden"
      style={{
        transform: sidebarOpen ? 'translateX(280px) translateZ(0)' : 'translateX(0) translateZ(0)',
        transition: 'transform 0.3s ease',
        width: '100%',
        height: '100vh',
        willChange: 'transform'
      }}
      onClick={() => sidebarOpen && setSidebarOpen(false)}
    >
      <div
        className="w-full flex flex-col"
        style={{
          paddingTop: 'var(--navbar-height)',
          paddingBottom: 'var(--footer-height)',
          height: '100vh',
          backgroundColor: 'var(--bg-primary)'
        }}
      >
        <ProjectsGrid
          projects={projects}
          onSelectProject={handleSelectProject}
          onCreateProject={handleCreateProject}
          onDeleteProject={handleDeleteProject}
          onRenameProject={handleRenameProject}
        />
      </div>
    </div>
  );
};

export default ProjectsPage;
